import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon, IconName, Drawer } from '../components/ui';
import {
  OrgRiskSummary,
  AgentRisk,
  RiskEvent,
  RiskSeverity,
  RiskCategory,
  getRiskSummary,
  getRiskEvents,
  getAgentTimeline,
  getDemoConfig,
  getAiInventory,
  AiInventory,
} from '../lib/riskApi';
import CommandCenterTheater from '../components/CommandCenterTheater';
import QuickstartPanel from '../components/QuickstartPanel';
import { usePermission } from '../hooks/usePermission';
import { Link } from 'react-router-dom';
import './CommandCenter.css';
import { useTranslation } from 'react-i18next';

interface CommandCenterProps {
  apiUrl: string;
}

const WINDOWS: { labelKey: string; hours: number }[] = [
  { labelKey: 'pages.commandCenter.window1h', hours: 1 },
  { labelKey: 'pages.commandCenter.window24h', hours: 24 },
  { labelKey: 'pages.commandCenter.window7d', hours: 168 },
];

const CATEGORY_ICON: Record<RiskCategory, IconName> = {
  injection: 'bolt',
  dlp: 'shield',
  denial: 'deny',
  rate_limit: 'signal',
  behaviour: 'view',
  shadow: 'search',
  other: 'info',
};

const CATEGORY_LABEL: Record<RiskCategory, string> = {
  injection: 'pages.commandCenter.catInjection',
  dlp: 'pages.commandCenter.catDlp',
  denial: 'pages.commandCenter.catDenial',
  rate_limit: 'pages.commandCenter.catRateLimit',
  behaviour: 'pages.commandCenter.catBehaviour',
  shadow: 'pages.commandCenter.catShadow',
  other: 'pages.commandCenter.catOther',
};

const SEV_KEY: Record<string, string> = {
  low: 'pages.commandCenter.sevLow',
  medium: 'pages.commandCenter.sevMedium',
  high: 'pages.commandCenter.sevHigh',
  critical: 'pages.commandCenter.sevCritical',
};
const sevLabel = (t: (key: string) => string, s: string) => (SEV_KEY[s] ? t(SEV_KEY[s]) : s);

const sevClass = (s: RiskSeverity) => `sev-${s}`;

const fmtBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
};

const fmtCount = (n: number): string => (n >= 10000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : String(n));

/** What AI is in use, who uses it, how much, and how much of it is protected. */
const InventoryBand: React.FC<{ inv: AiInventory }> = ({ inv }) => {
  const { t } = useTranslation();
  const tiles: { label: string; n: string; sub: string; icon: IconName; hot?: boolean }[] = [
    { label: t('pages.commandCenter.invApps'), n: fmtCount(inv.apps.total), sub: t('pages.commandCenter.invAppsSub', { count: inv.apps.unsanctioned }), icon: 'search', hot: inv.apps.unsanctioned > 0 },
    { label: t('pages.commandCenter.invAgents'), n: fmtCount(inv.agents.registered + inv.agents.shadow), sub: t('pages.commandCenter.invAgentsSub', { count: inv.agents.shadow }), icon: 'view', hot: inv.agents.shadow > 0 },
    { label: t('pages.commandCenter.invMcp'), n: fmtCount(inv.mcp.total), sub: t('pages.commandCenter.invMcpSub', { gw: inv.mcp.gateway, un: inv.mcp.unprotected }), icon: 'shield', hot: inv.mcp.unprotected > 0 },
    { label: t('pages.commandCenter.invUsers'), n: fmtCount(inv.users), sub: t('pages.commandCenter.invUsersSub'), icon: 'info' },
    { label: t('pages.commandCenter.invComms'), n: fmtCount(inv.communications), sub: t('pages.commandCenter.invCommsSub'), icon: 'signal' },
    { label: t('pages.commandCenter.invVolume'), n: fmtBytes(inv.bytesIn + inv.bytesOut), sub: t('pages.commandCenter.invVolumeSub', { out: fmtBytes(inv.bytesOut), in: fmtBytes(inv.bytesIn) }), icon: 'refresh' },
    { label: t('pages.commandCenter.invViolations'), n: fmtCount(inv.violations), sub: t('pages.commandCenter.invViolationsSub'), icon: 'deny', hot: inv.violations > 0 },
  ];
  return (
    <div className="cc-card cc-inv">
      <div className="cc-inv-head">
        <div>
          <div className="cc-inv-title">{t('pages.commandCenter.invTitle')}</div>
          <div className="cc-inv-sub">{t('pages.commandCenter.invSubtitle')}</div>
        </div>
      </div>
      <div className="cc-inv-tiles">
        {tiles.map((x) => (
          <div key={x.label} className={`cc-inv-tile ${x.hot ? 'hot' : ''}`}>
            <span className="cc-inv-l"><Icon name={x.icon} size={13} /> {x.label}</span>
            <span className="cc-inv-n">{x.n}</span>
            <span className="cc-inv-s">{x.sub}</span>
          </div>
        ))}
      </div>
      {inv.topApps.length > 0 ? (
        <div className="cc-inv-table-wrap">
          <div className="cc-card-label">{t('pages.commandCenter.invTopApps')}</div>
          <table className="cc-inv-table">
            <thead>
              <tr>
                <th>{t('pages.commandCenter.invEndpoint')}</th>
                <th>{t('pages.commandCenter.invProvider')}</th>
                <th className="num">{t('pages.commandCenter.invUsers')}</th>
                <th className="num">{t('pages.commandCenter.invHits')}</th>
                <th className="num">↑ / ↓</th>
              </tr>
            </thead>
            <tbody>
              {inv.topApps.map((a) => (
                <tr key={a.endpoint}>
                  <td><code>{a.endpoint}</code></td>
                  <td>
                    {a.provider || '-'}{' '}
                    <span className={`cc-inv-pill ${a.managed ? 'ok' : 'warn'}`}>
                      {a.managed ? t('pages.commandCenter.invManaged') : t('pages.commandCenter.invUnmanaged')}
                    </span>
                  </td>
                  <td className="num">{a.users}</td>
                  <td className="num">{fmtCount(a.hits)}</td>
                  <td className="num">{fmtBytes(a.bytesOut)} / {fmtBytes(a.bytesIn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="cc-inv-empty">{t('pages.commandCenter.invNoTraffic')}</div>
      )}
      {inv.mcp.unprotectedHosts.length > 0 && (
        <div className="cc-inv-unprotected">
          <Icon name="warning" size={13} /> {t('pages.commandCenter.invUnprotected')}{' '}
          {inv.mcp.unprotectedHosts.map((h) => <code key={h}>{h}</code>)}
        </div>
      )}
    </div>
  );
};

function timeAgo(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return t('pages.commandCenter.secondsAgo', { count: secs });
  const mins = Math.round(secs / 60);
  if (mins < 60) return t('pages.commandCenter.minutesAgo', { count: mins });
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return t('pages.commandCenter.hoursAgo', { count: hrs });
  return t('pages.commandCenter.daysAgo', { count: Math.round(hrs / 24) });
}

/** Semicircular risk gauge (0–100). Uses pathLength=100 so the arc fill == score. */
const RiskGauge: React.FC<{ score: number; severity: RiskSeverity }> = ({ score, severity }) => {
  const { t } = useTranslation();
  return (
  <div className={`cc-gauge ${sevClass(severity)}`}>
    <svg viewBox="0 0 200 116" className="cc-gauge-svg">
      <path className="cc-gauge-track" d="M 16 100 A 84 84 0 0 1 184 100" pathLength={100} />
      <path
        className="cc-gauge-fill"
        d="M 16 100 A 84 84 0 0 1 184 100"
        pathLength={100}
        strokeDasharray={`${score} 100`}
      />
    </svg>
    <div className="cc-gauge-center">
      <span className="cc-gauge-score">{score}</span>
      <span className="cc-gauge-sev">{severity === 'none' ? t('pages.commandCenter.clear') : sevLabel(t, severity)}</span>
    </div>
  </div>
);
};

const SignalChips: React.FC<{ agent: AgentRisk }> = ({ agent }) => {
  const { t } = useTranslation();
  const chips: { key: RiskCategory; n: number }[] = [
    { key: 'injection', n: agent.signals.injections },
    { key: 'dlp', n: agent.signals.dlp },
    { key: 'denial', n: agent.signals.denials },
    { key: 'rate_limit', n: agent.signals.rateLimits },
    { key: 'shadow', n: agent.signals.shadowHits },
  ].filter((c) => c.n > 0);
  return (
    <div className="cc-chips">
      {agent.signals.maxDrift > 0 && (
        <span className="cc-chip" title={t('pages.commandCenter.behaviourDriftScore')}>
          <Icon name="view" size={12} /> {t('pages.commandCenter.drift')} {agent.signals.maxDrift}
        </span>
      )}
      {chips.map((c) => (
        <span key={c.key} className="cc-chip" title={t(CATEGORY_LABEL[c.key])}>
          <Icon name={CATEGORY_ICON[c.key]} size={12} /> {c.n}
        </span>
      ))}
    </div>
  );
};

const CommandCenter: React.FC<CommandCenterProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [windowHours, setWindowHours] = useState(24);
  const [summary, setSummary] = useState<OrgRiskSummary | null>(null);
  const [inventory, setInventory] = useState<AiInventory | null>(null);
  const [events, setEvents] = useState<RiskEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selected, setSelected] = useState<AgentRisk | null>(null);
  const [timeline, setTimeline] = useState<RiskEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const canProvision = usePermission('manage:settings');

  const load = useCallback(
    async (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      try {
        const [s, e, inv] = await Promise.all([
          getRiskSummary(apiUrl, windowHours),
          getRiskEvents(apiUrl, 40),
          getAiInventory(apiUrl, windowHours).catch(() => null),
        ]);
        setSummary(s);
        setEvents(e);
        setInventory(inv);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load risk data');
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, windowHours]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  useEffect(() => {
    getDemoConfig(apiUrl).then(setDemoEnabled).catch(() => setDemoEnabled(false));
  }, [apiUrl]);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (autoRefresh) {
      timer.current = setInterval(() => load(false), 5000);
    }
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [autoRefresh, load]);

  const openTimeline = async (agent: AgentRisk) => {
    setSelected(agent);
    setTimelineLoading(true);
    try {
      setTimeline(await getAgentTimeline(apiUrl, agent.agentId, windowHours));
    } catch {
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const totals = summary?.totals;
  const counters: { key: RiskCategory | 'anomalies'; label: string; n: number; icon: IconName }[] = [
    { key: 'injection', label: t('pages.commandCenter.injections'), n: totals?.injections || 0, icon: 'bolt' },
    { key: 'dlp', label: t('pages.commandCenter.dataLeaks'), n: totals?.dlp || 0, icon: 'shield' },
    { key: 'denial', label: t('pages.commandCenter.policyDenials'), n: totals?.denials || 0, icon: 'deny' },
    { key: 'rate_limit', label: t('pages.commandCenter.rateLimits'), n: totals?.rateLimits || 0, icon: 'signal' },
    { key: 'shadow', label: t('pages.commandCenter.shadowAI'), n: totals?.shadow || 0, icon: 'search' },
    { key: 'anomalies', label: t('pages.commandCenter.anomalies'), n: totals?.anomalies || 0, icon: 'view' },
  ];

  return (
    <div className="page-content cc">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="signal" size={20} /> {t('pages.commandCenter.aiSecurityCommandCenter')}
          </h1>
          <p className="subtitle">{t('pages.commandCenter.unifiedRealTimeRiskAcrossEvery')}</p>
        </div>
        <div className="cc-controls">
          <div className="cc-window">
            {WINDOWS.map((w) => (
              <button
                key={w.hours}
                className={windowHours === w.hours ? 'active' : ''}
                onClick={() => setWindowHours(w.hours)}
              >
                {t(w.labelKey)}
              </button>
            ))}
          </div>
          <button
            className={`cc-live ${autoRefresh ? 'on' : ''}`}
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh ? t('pages.commandCenter.liveUpdatesOnEvery5s') : t('pages.commandCenter.liveUpdatesPaused')}
          >
            <span className="cc-live-dot" /> {autoRefresh ? t('pages.commandCenter.live') : t('pages.commandCenter.paused')}
          </button>
          <button className="ent-btn ghost sm" onClick={() => load(true)} title={t('pages.commandCenter.refreshNow')}>
            <Icon name="refresh" size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="cc-error">
          <Icon name="warning" size={16} /> {error}
        </div>
      )}

      {canProvision && <QuickstartPanel apiUrl={apiUrl} demoEnabled={demoEnabled} onChange={() => load(false)} />}

      {demoEnabled && <CommandCenterTheater apiUrl={apiUrl} onBeat={() => load(false)} />}

      {inventory && <InventoryBand inv={inventory} />}

      {/* Headline: gauge + counters */}
      <div className="cc-top">
        <div className={`cc-card cc-gauge-card ${summary ? sevClass(summary.topSeverity) : ''}`}>
          <div className="cc-card-label">{t('pages.commandCenter.highestAgentRisk')}</div>
          <RiskGauge score={summary?.topScore || 0} severity={summary?.topSeverity || 'none'} />
          <div className="cc-gauge-foot">
            {t('pages.commandCenter.agentsAtRisk', { count: summary?.agentsAtRisk || 0 })}
          </div>
        </div>
        <div className="cc-counters">
          {counters.map((c) => (
            <div key={c.key} className={`cc-card cc-counter ${c.n > 0 ? 'hot' : ''}`}>
              <Icon name={c.icon} size={18} />
              <span className="cc-counter-n">{c.n}</span>
              <span className="cc-counter-l">{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Body: agents at risk + live stream */}
      <div className="cc-body">
        <div className="cc-card cc-agents">
          <div className="cc-card-head">
            <h3>{t('pages.commandCenter.agentsAtRisk')}</h3>
            <span className="cc-count-pill">{summary?.topAgents.length || 0}</span>
          </div>
          {loading && !summary ? (
            <div className="cc-empty">{t('pages.commandCenter.loading')}</div>
          ) : (summary?.topAgents.length || 0) === 0 ? (
            <div className="cc-empty">
              <Icon name="checkCircle" size={20} /> {t('pages.commandCenter.noElevatedRiskInThisWindow')}
            </div>
          ) : (
            <div className="cc-agent-list">
              {summary!.topAgents.map((a) => (
                <button key={a.agentId} className="cc-agent-row" onClick={() => openTimeline(a)}>
                  <div className={`cc-score-badge ${sevClass(a.severity)}`}>{a.score}</div>
                  <div className="cc-agent-main">
                    <div className="cc-agent-name">
                      {a.name}
                      {a.status === 'suspended' && <span className="cc-quarantined">{t('pages.commandCenter.quarantined')}</span>}
                    </div>
                    <SignalChips agent={a} />
                  </div>
                  <div className="cc-agent-meta">
                    <span className={`cc-sev-tag ${sevClass(a.severity)}`}>{sevLabel(t, a.severity)}</span>
                    {a.lastEvent && <span className="cc-agent-time">{timeAgo(a.lastEvent, t)}</span>}
                    <Icon name="chevronRight" size={16} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="cc-card cc-stream">
          <div className="cc-card-head">
            <h3>{t('pages.commandCenter.liveThreatStream')}</h3>
            {autoRefresh && <span className="cc-live-dot" />}
          </div>
          {events.length === 0 ? (
            <div className="cc-empty">
              <Icon name="checkCircle" size={20} /> {t('pages.commandCenter.noThreatEventsYet')}
            </div>
          ) : (
            <div className="cc-event-list">
              {events.map((ev, i) => (
                <div key={i} className={`cc-event cat-${ev.category}`}>
                  <span className="cc-event-icon">
                    <Icon name={CATEGORY_ICON[ev.category]} size={14} />
                  </span>
                  <div className="cc-event-main">
                    <div className="cc-event-top">
                      <span className="cc-event-cat">{t(CATEGORY_LABEL[ev.category])}</span>
                      <span className="cc-event-time">{timeAgo(ev.timestamp, t)}</span>
                    </div>
                    <div className="cc-event-sub">
                      <span className="cc-event-agent">{ev.agentId}</span>
                      <span className="cc-event-label">{ev.label}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <Drawer
          title={t('pages.commandCenter.attackTimeline', { name: selected.name })}
          onClose={() => setSelected(null)}
          width={460}
          footer={
            // Containment actions live on Agent Management now (one page for
            // approve/reject/quarantine/release/deactivate/delete) — this
            // view stays read-only risk, with a direct link over to act on it.
            <Link className="ent-btn" to="/agents">
              <Icon name="chevronRight" size={16} /> {t('pages.commandCenter.manageThisAgent')}
            </Link>
          }
        >
          <div className="cc-tl-summary">
            <div className={`cc-score-badge lg ${sevClass(selected.severity)}`}>{selected.score}</div>
            <div>
              <div className="cc-tl-name">{selected.name}</div>
              <div className="cc-tl-id">{selected.agentId}</div>
            </div>
          </div>
          {timelineLoading ? (
            <div className="cc-empty">{t('pages.commandCenter.loadingTimeline')}</div>
          ) : timeline.length === 0 ? (
            <div className="cc-empty">{t('pages.commandCenter.noEventsInThisWindow')}</div>
          ) : (
            <ol className="cc-timeline">
              {timeline.map((ev, i) => (
                <li key={i} className={`cc-tl-item cat-${ev.category}`}>
                  <span className="cc-tl-dot">
                    <Icon name={CATEGORY_ICON[ev.category]} size={12} />
                  </span>
                  <div className="cc-tl-body">
                    <div className="cc-tl-head">
                      <span className="cc-tl-cat">{t(CATEGORY_LABEL[ev.category])}</span>
                      <span className="cc-tl-time">{timeAgo(ev.timestamp, t)}</span>
                    </div>
                    <div className="cc-tl-label">{ev.label}</div>
                    {ev.detail && <div className="cc-tl-detail">{ev.detail}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Drawer>
      )}
    </div>
  );
};

export default CommandCenter;
